import React, { useState } from 'react';
import Button from '../../../../../common/Button';
import { Modal, notification } from 'antd';
import { Formik, ErrorMessage } from 'formik';
import Form from 'formik-antd/lib/form';
import SubmitButton from '../../../../../common/SubmitButton';
import { string, object } from 'yup';
import Api from '../../../../services/Api';
import {
  Flex,
  FieldError,
  openNotificationWithIcon,
  useFetch,
  Group,
  User,
  Loading,
} from '@mss/mss-component-library';

import Select from 'formik-antd/lib/select';
import { useTranslation } from 'react-i18next';

type AddGroupModalProps = {
  closeModal: () => void;
  user: User;
  fetchGroupInfo: () => void;
};
type EnhancedGroup = {
  id: number;
  name: string;
  permissions: number[];
  system_id: string;
};

export default function AddGroupModal({
  user,
  closeModal,
  fetchGroupInfo,
}: AddGroupModalProps) {
  const [filteredGroups, setFilteredGroups] = useState<any[]>([]);
  const [t] = useTranslation(['authorization', 'common']);
  const [groupInfo] = useFetch({
    url: `/api/group/`,
    defaultValue: [],
    onSucces: ({ data }) => {
      setFilteredGroups(
        data
          .filter(
            (x: EnhancedGroup) => !user.groups.includes(x.id) && !x.system_id
          )
          .map((y: Group) => ({ key: y.id, value: y.id, label: y.name }))
      );
    },
  });

  return (
    <Modal
      title={t(`Add group to this user`)}
      visible
      closable={false}
      footer={[
        <Button ghost variant="primary" onClick={closeModal}>
          {t('Cancel')}
        </Button>,
      ]}
    >
      {groupInfo.loading && <Loading />}
      {filteredGroups.length === 0 && (
        <h4>{t('No groups to add this user to.')}</h4>
      )}
      {filteredGroups.length > 0 && (
        <>
          <p>
            {t(
              'Use the dropdown below to select a group you would like to add to this user.'
            )}
          </p>
          <Formik
            onSubmit={async (values: any) => {
              const result = await Api.group.user.add({
                user: user.id,
                group: values.group,
              });
              if (result.status >= 200 && result.status < 300) {
                fetchGroupInfo();
                openNotificationWithIcon(
                  notification.success,
                  t('Group added'),
                  t('Group successfully added to the user.')
                );
                closeModal();
              } else {
                openNotificationWithIcon(
                  notification.error,
                  t('Adding user failed'),
                  t(
                    'Due to technical issues the group could not be added to the user. Please try again later.'
                  )
                );
              }
            }}
            initialValues={{ group: '' }}
            validationSchema={object().shape({
              group: string().required(t('Please select a group')),
            })}
          >
            {({ values, errors }) => {
              return (
                <Form>
                  <Flex h="between" v="center">
                    <Select
                      name="group"
                      placeholder={t('Select a group')}
                      value={values.group}
                      data-ta="userGroupSelect"
                      optionFilterProp={'children'}
                      disabled={groupInfo.loading}
                      style={{ width: 375 }}
                      showSearch
                      autoFocus
                    >
                      {filteredGroups.map(x => (
                        <Select.Option key={x.value} value={x.value}>
                          {x.label}
                        </Select.Option>
                      ))}
                    </Select>
                    <SubmitButton
                      disabled={!!errors.group || !values.group}
                      style={{ flex: '0 0 80px' }}
                    >
                      {t('Add')}
                    </SubmitButton>
                  </Flex>
                  <FieldError>
                    <ErrorMessage name={'name'} />
                  </FieldError>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </Modal>
  );
}
